'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Search, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Title, Text } from '@/components/ui/Headings';
import GuidesCarousel from '@/components/GuidesCarousel';

interface Guide {
  id: number;
  type: string;
  slug: string;
  title: Record<string, string>;
  excerpt: Record<string, string>;
  heroImage: string;
  thumbnailImage: string | null;
  isFeatured: boolean;
}

interface TravelGuidesClientProps {
  featuredGuides: Guide[];
  allGuides: Guide[];
}

export default function TravelGuidesClient({ featuredGuides, allGuides }: TravelGuidesClientProps) {
  const { i18n, t } = useTranslation();
  const locale = (i18n.language || 'en') as string;
  
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'country' | 'city'>('all');

  // Filter guides based on search and type
  const filteredGuides = useMemo(() => {
    let guides = allGuides;
    
    // Filter by type
    if (filterType !== 'all') {
      guides = guides.filter(guide => guide.type === filterType);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      guides = guides.filter(guide => {
        const title = (guide.title[locale] || guide.title.en || '').toLowerCase();
        const excerpt = (guide.excerpt[locale] || guide.excerpt.en || '').toLowerCase();
        return title.includes(query) || excerpt.includes(query);
      });
    }
    
    return guides;
  }, [allGuides, searchQuery, filterType, locale]);
  
  const countries = filteredGuides.filter(g => g.type === 'country');
  const cities = filteredGuides.filter(g => g.type === 'city');

  return (
    <div className="pt-16">
      {/* Hero Section with Search */}
      <section className="py-8 bg-gradient-to-br from-primary-50 to-neutral-cream">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Title size="lg" className="mb-3">
            {t('travelGuides.index.title')}
          </Title>
          <Text variant="subtitle" className="text-gray-700 leading-relaxed mb-8">
            {t('travelGuides.index.subtitle')}
          </Text>
          
          {/* Search and Filter Bar */}
            <div className="max-w-2xl mx-auto space-y-4">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('travelGuides.index.search')}
                className="w-full pl-12 pr-4 py-3 rounded-full border border-gray-200 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 bg-white text-base"
              />
            </div>
            
            {/* Filter Buttons */}
            <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
              <button
                onClick={() => setFilterType('all')}
                className={`w-full sm:w-auto px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                  filterType === 'all'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                {t('travelGuides.index.allDestinations')}
              </button>
              <button
                onClick={() => setFilterType('country')}
                className={`w-full sm:w-auto px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                  filterType === 'country'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                {t('travelGuides.index.countries')}
              </button>
              <button
                onClick={() => setFilterType('city')}
                className={`w-full sm:w-auto px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                  filterType === 'city'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                {t('travelGuides.index.cities')}
              </button>
            </div>
            
            {/* Results count */}
            {(searchQuery || filterType !== 'all') && (
              <Text variant="caption" className="text-gray-600">
                Found {filteredGuides.length} {filteredGuides.length === 1 ? 'guide' : 'guides'}
              </Text>
            )}
          </div>
        </div>
      </section>

      {/* Featured Guides - Show only if no search/filter applied */}
      {!searchQuery && filterType === 'all' && featuredGuides.length > 0 && (
        <section className="py-8 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <Title size="md" className="mb-2">
                Discover our most popular destinations
              </Title>
            </div>
            
            {/* Carousel for all featured guides */}
            {featuredGuides.length > 0 && (
              <GuidesCarousel guides={featuredGuides} locale={locale} />
            )}
          </div>
        </section>
      )}

      {/* Countries Section */}
      {(filterType === 'all' || filterType === 'country') && countries.length > 0 && (
        <section className="py-8 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <Title size="md" className="mb-2">
                Explore comprehensive travel guides for countries around the world
              </Title>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 sm:gap-4">
              {countries.map((guide) => {
                const title = guide.title[locale] || guide.title.en || '';
                
                return (
                  <Link
                    key={guide.id}
                    href={`/${locale}/travel-guides/${guide.slug}`}
                    className="group block bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 aspect-[4/5] sm:aspect-square"
                  >
                    <div className="relative w-full h-full overflow-hidden">
                      <Image
                        src={guide.thumbnailImage || guide.heroImage}
                        alt={title}
                        fill
                        className="object-cover transition-transform duration-300 group-hover:scale-105"
                        sizes="(max-width: 640px) 50vw, (max-width: 768px) 33vw, (max-width: 1024px) 25vw, 16vw"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                      <div className="absolute bottom-2 left-2 right-2 sm:bottom-3 sm:left-3 sm:right-3">
                        <Title size="sm" className="text-white mb-0 text-base sm:text-lg font-semibold">
                          {title}
                        </Title>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Cities Section */}
      {(filterType === 'all' || filterType === 'city') && cities.length > 0 && (
        <section className="py-8 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <Title size="md" className="mb-2">
                Discover city guides with local insights and hidden gems
              </Title>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 sm:gap-4">
              {cities.map((guide) => {
                const title = guide.title[locale] || guide.title.en || '';
                
                return (
                  <Link
                    key={guide.id}
                    href={`/${locale}/travel-guides/${guide.slug}`}
                    className="group block bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 aspect-[4/5] sm:aspect-square"
                  >
                    <div className="relative w-full h-full overflow-hidden">
                      <Image
                        src={guide.thumbnailImage || guide.heroImage}
                        alt={title}
                        fill
                        className="object-cover transition-transform duration-300 group-hover:scale-105"
                        sizes="(max-width: 640px) 50vw, (max-width: 768px) 33vw, (max-width: 1024px) 25vw, 16vw"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                      <div className="absolute bottom-2 left-2 right-2 sm:bottom-3 sm:left-3 sm:right-3">
                        <Title size="sm" className="text-white mb-0 text-base sm:text-lg font-semibold">
                          {title}
                        </Title>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* No Results */}
      {filteredGuides.length === 0 && (
        <section className="py-8 bg-gray-50">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <Title size="sm" className="text-gray-600 mb-2">
              {t('travelGuides.index.noResults')}
            </Title>
            <Text variant="body" className="text-gray-500 mb-6">
              {t('travelGuides.index.noResultsDescription')}
            </Text>
            <button
              onClick={() => {
                setSearchQuery('');
                setFilterType('all');
              }}
              className="inline-block px-5 py-2 bg-primary-600 text-white rounded-full text-sm font-medium hover:bg-primary-700 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-10 bg-primary-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Title size="md" className="text-gray-900 mb-2">
            Ready to plan your journey?
          </Title>
          <Text variant="subtitle" className="text-gray-900 mb-8">
            Combine these destination insights with our signature journeys for the perfect trip
          </Text>
          <Link
            href={`/${locale}/signature-journeys`}
            className="inline-block bg-primary-600 text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-primary-700 transition-colors duration-300"
          >
            Browse Signature Journeys
          </Link>
        </div>
      </section>
    </div>
  );
}

